import React from 'react';
import { PageHeader, Badge, Stat } from './shared';
import { ModalHeader, ModalShell } from './Modals';
import { PipelineArrow, PipelineStage } from './Normalizers';
import { Icon } from './ui/icons';

function cx(...classes: (string | undefined | false)[]): string {
  return classes.filter(Boolean).join(' ');
}

// Conformance rules — declarative mappings between Controls and Framework requirements.
//
// Each rule is a logical predicate (AND / OR / threshold / tag-based / derived /
// with-exception). Multiple rules can satisfy the same requirement; one rule can
// satisfy requirements in many frameworks.
//
// Architecturally these are TypeDB-style inference rules; the detail panel shows
// the equivalent TypeQL so you can reason about derived facts.

// ── Types and Interfaces ─────────────────────────────────────────────────────

interface Control {
  id: string;
  name: string;
  status: string;
  pass: number;
  fail: number;
  pending: number;
  tags?: string[];
}

interface Framework {
  id: string;
  shortName: string;
  color: string;
}

interface Repository {
  id: string;
  name: string;
}

interface Satisfaction {
  framework: string;
  requirement: string;
}

interface Operand {
  kind: string;
  control?: string;
  tag?: string;
}

interface Condition {
  op: string;
  control?: string;
  operands?: Operand[];
  threshold?: number;
  scope?: string;
  source?: {
    requirement: string;
  };
}

interface ConformanceRule {
  id: string;
  name: string;
  description: string;
  kind: string;
  version: string;
  condition: Condition;
  satisfies: Satisfaction[];
  owner: string;
  lastEvaluated: string | null;
  status: string;
  typeql: string;
}

interface ConformanceData {
  conformance?: {
    rules: ConformanceRule[];
  };
  controls: {
    controls: Control[];
  };
  frameworks: {
    frameworks: Framework[];
  };
  repos: {
    repos: Repository[];
  };
}

interface ConformanceProps {
  data: ConformanceData;
  navigate: (route: string) => void;
}

interface EvaluationResult {
  ok: boolean;
  basis?: string[];
  kind?: string;
  parts?: EvaluationResult[];
  ratio?: number;
  derived?: boolean;
  source?: string;
}

// ── Utility Functions ────────────────────────────────────────────────────────

export function timeAgo(iso: string): string {
  const t = new Date(iso).getTime();
  const diff = Date.now() - t;
  const m = Math.round(diff / 60000);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
}

const KIND_TONE = {
  simple: { tone: 'muted' as const, label: 'Simple' },
  'all-of': { tone: 'info' as const, label: 'All of' },
  'any-of': { tone: 'info' as const, label: 'Any of' },
  'tag-based': { tone: 'draft' as const, label: 'Tag-based' },
  threshold: { tone: 'warn' as const, label: 'Threshold' },
  'with-exception': { tone: 'muted' as const, label: 'With exception' },
  derived: { tone: 'draft' as const, label: 'Derived' },
};

const STATUS_TONE = {
  active: 'ok' as const,
  draft: 'draft' as const,
  changed: 'warn' as const,
  paused: 'muted' as const,
};

function evaluateRule(rule: ConformanceRule, controls: Control[], _repos: Repository[]): EvaluationResult {
  const passing = (c: Control | undefined) =>
    c &&
    c.status === 'published' &&
    c.pass + c.fail + c.pending > 0 &&
    c.pass / (c.pass + c.fail + c.pending) >= 0.85;

  const evalOperand = (op: Operand): EvaluationResult => {
    if (op.kind === 'control-passing') {
      if (op.tag) {
        const ctrls = controls.filter((c) => c.tags?.includes(op.tag));
        return {
          ok: ctrls.length > 0 && ctrls.every(passing),
          basis: ctrls.map((c) => c.id),
          kind: 'tag',
        };
      }
      const c = controls.find((x) => x.id === op.control);
      return { ok: !!c && passing(c), basis: c ? [c.id] : [], kind: 'control' };
    }
    if (op.kind === 'no-active-risk-acceptance') {
      return {
        ok: true,
        basis: [`no-active-RA on ${op.control}`],
        kind: 'negation',
      };
    }
    return { ok: false, basis: [] };
  };

  if (rule.condition.op === 'control-passing') {
    return evalOperand({
      kind: 'control-passing',
      control: rule.condition.control,
    });
  }
  if (rule.condition.op === 'all-of') {
    const results = (rule.condition.operands || []).map(evalOperand);
    return { ok: results.every((r) => r.ok), parts: results };
  }
  if (rule.condition.op === 'any-of') {
    const results = (rule.condition.operands || []).map(evalOperand);
    return { ok: results.some((r) => r.ok), parts: results };
  }
  if (rule.condition.op === 'threshold') {
    // Simplified: pretend `passing repos / total >= threshold` is met if most controls pass
    const results = (rule.condition.operands || []).map(evalOperand);
    const ratio = results.filter((r) => r.ok).length / results.length;
    return { ok: ratio >= (rule.condition.threshold || 0), parts: results, ratio };
  }
  if (rule.condition.op === 'derived-from') {
    return { ok: true, derived: true, source: rule.condition.source?.requirement };
  }
  return { ok: false };
}

function ConditionTree({ 
  cond, 
  controls, 
  indent = 0 
}: { 
  cond: Condition; 
  controls: Control[]; 
  indent?: number;
}) {
  const pad = { paddingLeft: indent * 12 };
  if (cond.op === 'control-passing') {
    return (
      <div style={pad} className="flex items-center gap-2">
        <Icon name="check" size={12} stroke="#159D4C" />
        <span className="font-data text-[12.5px] text-ink/80">
          control_passing(<b className="font-bold text-ink">#{cond.control}</b>)
        </span>
      </div>
    );
  }
  if (cond.op === 'all-of' || cond.op === 'any-of') {
    const label = cond.op === 'all-of' ? 'ALL_OF' : 'ANY_OF';
    const color = cond.op === 'all-of' ? '#0A69DA' : '#CA9401';
    return (
      <div style={pad} className="flex flex-col gap-1.5">
        <div className="inline-flex items-center gap-2">
          <span
            className="font-data text-[10px] font-bold px-1.5 py-[1px] rounded-[3px] text-white"
            style={{ background: color }}
          >
            {label}
          </span>
        </div>
        {(cond.operands || []).map((o, i) => (
          <ConditionTree
            key={`${o.control || 'operand'}-${i}`}
            cond={{ op: 'control-passing', ...o }}
            controls={controls}
            indent={indent + 1}
          />
        ))}
      </div>
    );
  }
  if (cond.op === 'threshold') {
    return (
      <div style={pad} className="flex flex-col gap-1.5">
        <div className="inline-flex items-center gap-2">
          <span className="font-data text-[10px] font-bold px-1.5 py-[1px] rounded-[3px] text-white bg-[#CA9401]">
            THRESHOLD ≥ {Math.round((cond.threshold || 0) * 100)}%
          </span>
          <span className="font-data text-[11px] text-ink/55">across {cond.scope}</span>
        </div>
        {(cond.operands || []).map((o, i) => (
          <ConditionTree
            key={`threshold-${o.control || 'operand'}-${i}`}
            cond={{ op: 'control-passing', ...o }}
            controls={controls}
            indent={indent + 1}
          />
        ))}
      </div>
    );
  }
  if (cond.op === 'derived-from') {
    return (
      <div style={pad} className="flex flex-col gap-2 font-data text-[12px]">
        <span className="inline-flex items-center gap-2">
          <span className="font-data text-[10px] font-bold px-1.5 py-[1px] rounded-[3px] text-white bg-[#7F56D9]">
            DERIVED-FROM
          </span>
        </span>
        <span className="text-ink/75">
          (control, requirement: <b className="font-bold text-ink">{cond.source?.requirement}</b>)
          isa satisfies-requirement
        </span>
      </div>
    );
  }
  return null;
}

function ConformanceRuleRow({ 
  rule, 
  frameworks, 
  onOpen 
}: { 
  rule: ConformanceRule; 
  frameworks: Framework[];
  onOpen: (rule: ConformanceRule) => void;
}) {
  const kind = KIND_TONE[rule.kind as keyof typeof KIND_TONE] || KIND_TONE.simple;
  return (
    <button
      type="button"
      onClick={() => onOpen(rule)}
      className="grid items-center px-5 py-4 border-b border-[#F7EDDB] last:border-0 hover:bg-[#FCF8F1] cursor-pointer text-left"
      style={{ gridTemplateColumns: '1.8fr 1fr 1.6fr 1fr 1fr 90px' }}
    >
      <div className="min-w-0">
        <div className="font-display font-bold text-ink text-[14px] truncate">{rule.name}</div>
        <div className="font-data text-[11px] text-ink/45 mt-0.5">
          #{rule.id} · {rule.version}
        </div>
      </div>
      <Badge tone={kind.tone}>{kind.label}</Badge>
      <div className="flex items-center gap-1.5 flex-wrap">
        {rule.satisfies.slice(0, 4).map((s) => {
          const f = frameworks.find((x) => x.id === s.framework);
          return (
            <span
              key={`${s.framework}-${s.requirement}`}
              className="inline-flex items-center gap-1.5 h-6 px-1.5 rounded-[5px]"
              style={{ background: f ? `${f.color}1a` : '#F7EDDB' }}
            >
              <span
                className="font-data text-[10px] font-bold"
                style={{ color: f?.color || '#6B7280' }}
              >
                {f?.shortName || s.framework}
              </span>
              <span className="font-data text-[11px] text-ink/70">{s.requirement}</span>
            </span>
          );
        })}
        {rule.satisfies.length > 4 && (
          <span className="font-data text-[11px] text-ink/55">+{rule.satisfies.length - 4}</span>
        )}
      </div>
      <span className="font-data text-[12px] text-ink/55">{rule.owner}</span>
      <span className="font-data text-[12px] text-ink/55">
        {rule.lastEvaluated ? timeAgo(rule.lastEvaluated) : <span className="text-ink/30">—</span>}
      </span>
      <Badge tone={STATUS_TONE[rule.status as keyof typeof STATUS_TONE] || 'muted'} dot>
        {rule.status}
      </Badge>
    </button>
  );
}

function ConformanceRulePanel({ 
  open, 
  rule, 
  data, 
  onClose, 
  navigate 
}: { 
  open: boolean; 
  rule: ConformanceRule | null; 
  data: ConformanceData;
  onClose: () => void;
  navigate: (route: string) => void;
}) {
  if (!rule) return null;
  const controls = data.controls.controls;
  const frameworks = data.frameworks.frameworks;
  const evaluation = evaluateRule(rule, controls, data.repos.repos);
  const kind = KIND_TONE[rule.kind as keyof typeof KIND_TONE] || KIND_TONE.simple;

  return (
    <ModalShell open={open} onClose={onClose} width={920}>
      <ModalHeader
        title={rule.name}
        subtitle={`#${rule.id} · ${rule.version} · ${kind.label}`}
        onClose={onClose}
        icon="git-branch"
      />
      <div className="px-6 py-5 flex flex-col gap-5 overflow-y-auto">
        <p className="font-display text-[14px] text-ink/75">{rule.description}</p>

        {/* Pipeline mini */}
        <div className="rounded-[10px] border border-[#F3E4C8] bg-[#FCF8F1] p-4">
          <div className="font-display text-[11px] font-bold uppercase tracking-[0.04em] text-ink/55 mb-3">
            Where this rule sits
          </div>
          <div
            className="grid items-center gap-1"
            style={{
              gridTemplateColumns: 'minmax(0,1fr) 28px minmax(0,1fr) 28px minmax(0,1fr)',
            }}
          >
            <PipelineStage title="Controls" sub={`${controls.length} in library`} icon="shield" />
            <PipelineArrow label="satisfy" />
            <PipelineStage
              title="Conformance rule"
              sub={rule.kind}
              icon="git-branch"
              accent="#7F56D9"
            />
            <PipelineArrow label="proves" />
            <PipelineStage
              title={`${rule.satisfies.length} requirement${rule.satisfies.length > 1 ? 's' : ''}`}
              sub={`${[...new Set(rule.satisfies.map((s) => s.framework))].length} framework(s)`}
              icon="shield"
              accent="#FA500E"
            />
          </div>
        </div>

        <div className="grid gap-4" style={{ gridTemplateColumns: '1.1fr 1fr' }}>
          {/* Condition tree */}
          <div className="rounded-[10px] border border-[#F3E4C8] bg-white p-4">
            <div className="font-display font-bold text-ink text-[13px] mb-3 flex items-center justify-between">
              <span>Condition</span>
              <Badge tone={evaluation.ok ? 'ok' : 'crit'} dot>
                {evaluation.ok ? 'satisfied right now' : 'not satisfied'}
              </Badge>
            </div>
            <ConditionTree cond={rule.condition} controls={controls} />
          </div>

          {/* What it satisfies */}
          <div className="rounded-[10px] border border-[#F3E4C8] bg-white p-4">
            <div className="font-display font-bold text-ink text-[13px] mb-3">Satisfies</div>
            <ul className="flex flex-col gap-1.5">
              {rule.satisfies.map((s) => {
                const f = frameworks.find((x) => x.id === s.framework);
                return (
                  <li key={`${s.framework}-${s.requirement}`}>
                    <button
                      type="button"
                      onClick={() => {
                        onClose();
                        navigate(`frameworks/${s.framework}`);
                      }}
                      className="w-full flex items-center gap-3 px-2 py-1.5 rounded-[6px] hover:bg-[#FCF8F1] text-left"
                    >
                      <span
                        className="h-6 w-6 rounded-[5px] grid place-items-center shrink-0"
                        style={{ background: f ? `${f.color}1a` : '#F7EDDB' }}
                      >
                        <Icon name="shield" size={11} stroke={f?.color || '#6B7280'} />
                      </span>
                      <span className="font-display font-bold text-ink text-[12px] w-[80px] shrink-0">
                        {f?.shortName}
                      </span>
                      <span className="font-data text-[12px] text-ink/65 truncate">
                        {s.requirement}
                      </span>
                      <Icon name="chevron-right" size={12} stroke="rgba(18,17,17,0.4)" />
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>

        {/* TypeQL equivalent */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <div className="font-display font-bold text-ink text-[14px]">TypeQL equivalent</div>
            <Badge tone="info">TypeDB · rule-based inference</Badge>
          </div>
          <pre className="rounded-[8px] border border-[#F3E4C8] bg-[#121111] text-[#F7EDDB] p-4 font-data text-[12.5px] leading-[1.6] overflow-x-auto">
            <span className="text-[#9CA3AF]">
              # Stored as a typedb rule — evaluated incrementally,
            </span>
            {'\n'}
            <span className="text-[#9CA3AF]"># derived facts are kept in the knowledge graph.</span>
            {'\n\n'}
            {rule.typeql}
          </pre>
        </div>
      </div>
      <div className="px-6 py-4 border-t border-[#F7EDDB] flex justify-between items-center">
        <span className="font-data text-[12px] text-ink/55">Owned by {rule.owner}</span>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onClose}
            className="h-10 px-4 rounded-[8px] border border-[#F3E4C8] font-display font-semibold text-[14px] hover:bg-[#FCF8F1]"
          >
            Close
          </button>
          <button
            type="button"
            onClick={() =>
              (window as any).notify?.('Rule editor', {
                sub: 'Opening the declarative rule editor…',
                tone: 'info',
              })
            }
            className="h-10 px-4 rounded-[8px] bg-[#FA500E] text-white font-display font-bold text-[14px] hover:opacity-90 whitespace-nowrap"
          >
            Edit rule
          </button>
        </div>
      </div>
    </ModalShell>
  );
}

export default function Conformance({ data, navigate }: ConformanceProps) {
  const [q, setQ] = React.useState('');
  const [kindFilter, setKindFilter] = React.useState('all');
  const [opened, setOpened] = React.useState<ConformanceRule | null>(null);
  const rules = data.conformance?.rules || [];

  const list = rules.filter((r) => {
    if (kindFilter !== 'all' && r.kind !== kindFilter) return false;
    if (!q.trim()) return true;
    return (r.name + r.id).toLowerCase().includes(q.toLowerCase());
  });

  return (
    <div className="flex flex-col gap-8">
      <PageHeader
        title="Conformance rules"
        subtitle="Declarative inference layer between Controls and Framework requirements. Powered by a typed knowledge graph — one rule can satisfy requirements across many frameworks."
        right={
          <button
            type="button"
            onClick={() => (window as any).openCreate?.('rule')}
            className="h-10 px-4 rounded-[10px] bg-[#FA500E] text-white font-display font-bold text-[15px] inline-flex items-center gap-2 hover:opacity-90 whitespace-nowrap shrink-0"
          >
            <Icon name="plus" size={16} stroke="#FFF" /> New rule
          </button>
        }
      />

      {/* Pipeline */}
      <div className="rounded-[10px] border border-[#F3E4C8] bg-white p-6">
        <div className="font-display font-bold text-[11px] uppercase tracking-[0.1em] text-ink/55 mb-4">
          Where conformance sits
        </div>
        <div
          className="grid items-center gap-1"
          style={{
            gridTemplateColumns:
              'minmax(0,1fr) 32px minmax(0,1fr) 32px minmax(0,1fr) 32px minmax(0,1fr)',
          }}
        >
          <PipelineStage title="Interpreter" sub="control verdict" icon="git-branch" />
          <PipelineArrow label="emits" />
          <PipelineStage title="Control" sub="passing / failing" icon="shield" />
          <PipelineArrow label="rule" />
          <PipelineStage
            title="Conformance"
            sub="all-of, any-of, tag…"
            icon="git-branch"
            accent="#7F56D9"
          />
          <PipelineArrow label="proves" />
          <PipelineStage
            title="Framework req."
            sub="CC6.6, Art. 32 …"
            icon="shield"
            accent="#FA500E"
          />
        </div>
      </div>

      <div className="grid grid-cols-4 gap-4">
        <Stat label="Rules" value={rules.length} />
        <Stat label="Active" value={rules.filter((r) => r.status === 'active').length} tone="ok" />
        <Stat
          label="Reqs covered"
          value={
            [...new Set(rules.flatMap((r) => r.satisfies.map((s) => s.framework + s.requirement)))]
              .length
          }
        />
        <Stat
          label="Frameworks"
          value={[...new Set(rules.flatMap((r) => r.satisfies.map((s) => s.framework)))].length}
        />
      </div>

      <div className="flex items-center justify-between gap-3 rounded-[4px] border border-[rgba(18,17,17,0.2)] px-5 h-12">
        <div className="flex items-center gap-3 flex-1">
          <Icon name="search" size={18} stroke="#121111" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Find a rule…"
            className="flex-1 bg-transparent outline-none font-display text-[14px] placeholder:text-ink/45"
          />
        </div>
        {q && (
          <button type="button" onClick={() => setQ('')}>
            <Icon name="x" size={18} stroke="#121111" />
          </button>
        )}
      </div>

      <div className="flex items-center gap-1 flex-wrap">
        <button
          type="button"
          onClick={() => setKindFilter('all')}
          className={cx(
            'px-3 h-9 rounded-[8px] font-display text-[13px] inline-flex items-center gap-2',
            kindFilter === 'all'
              ? 'bg-ink text-white font-semibold'
              : 'text-ink/70 hover:bg-[#F7EDDB]'
          )}
        >
          All{' '}
          <span className="font-data text-[11px] rounded-full px-1.5 min-w-[20px] text-center bg-white/15">
            {rules.length}
          </span>
        </button>
        {Object.entries(KIND_TONE).map(([id, k]) => {
          const n = rules.filter((r) => r.kind === id).length;
          if (!n) return null;
          return (
            <button
              type="button"
              key={id}
              onClick={() => setKindFilter(id)}
              className={cx(
                'px-3 h-9 rounded-[8px] font-display text-[13px] inline-flex items-center gap-2',
                kindFilter === id
                  ? 'bg-ink text-white font-semibold'
                  : 'text-ink/70 hover:bg-[#F7EDDB]'
              )}
            >
              {k.label} <span className="font-data text-[11px] text-ink/45">{n}</span>
            </button>
          );
        })}
      </div>

      <div className="rounded-[6px] border border-[#F3E4C8] bg-white overflow-hidden">
        <div
          className="grid items-center px-5 h-[54px] border-b border-[#F7EDDB] bg-[#FCF8F1] font-display font-bold text-[13px] text-ink"
          style={{ gridTemplateColumns: '1.8fr 1fr 1.6fr 1fr 1fr 90px' }}
        >
          <span>Rule</span>
          <span>Kind</span>
          <span>Satisfies</span>
          <span>Owner</span>
          <span>Last evaluated</span>
          <span>Status</span>
        </div>
        <ul>
          {list.map((rule) => (
            <ConformanceRuleRow
              key={rule.id}
              rule={rule}
              frameworks={data.frameworks.frameworks}
              onOpen={setOpened}
            />
          ))}
        </ul>
      </div>

      <ConformanceRulePanel
        open={!!opened}
        rule={opened}
        data={data}
        onClose={() => setOpened(null)}
        navigate={navigate}
      />
    </div>
  );
}